import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Form,
  Button,
  Card,
  Row,
  Col,
  InputGroup,
  Alert,
  Badge,
} from "react-bootstrap";
import {
  Calendar,
  ShoppingCart,
  Truck,
  Save,
  ArrowLeft,
  Percent,
  Hash,
} from "lucide-react";
import {
  getOrderById,
  getAllOrders,
  getAllDeliveryNotes,
  getDeliveryNoteById,
} from "../../api/orders";
import {
  createInvoice,
  getInvoiceById,
  updateInvoice,
} from "../../api/invoices";
import { formatCurrency } from "../../utils/formatters";
import Loading from "../common/Loading";

const InvoiceForm = ({ onCancel, onSuccess, preselectedDeliveryNoteId }) => {

  const { id } = useParams();
  const isEditMode = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [deliveryNotes, setDeliveryNotes] = useState([]);
  const [selectedDeliveryNote, setSelectedDeliveryNote] = useState(null);
  const [blByOrder, setBlByOrder] = useState({});

  const [formData, setFormData] = useState({
    orderId: "",
    deliveryNoteId: "",
    issueDate: new Date().toISOString().split("T")[0],
    dueDate: "",
    discountType: "fixed", // 'fixed' ou 'percent'
    discountAmount: "0",
    discountPercent: "0",
    notes: "",
    paymentTerms: "30",
    status: "PENDING",
  });

  const [calculatedAmounts, setCalculatedAmounts] = useState({
    totalHT: 0,
    totalAfterDiscount: 0,
    discountValue: 0,
    TVA: 0,
    totalTTC: 0,
  });

  // ───────────────────────── Chargement initial ─────────────────────────
  useEffect(() => {
    if (isEditMode) {
      fetchInvoiceDetails();
    } else {
      fetchBillableData();
    }
  }, [id]);

  // Pré-sélection quand on arrive depuis le détail d'un BL
  useEffect(() => {
    if (!preselectedDeliveryNoteId || formData.deliveryNoteId) return;

    const dnId = String(preselectedDeliveryNoteId);
    let foundOrderId = null;
    let foundBl = null;
    Object.entries(blByOrder).forEach(([orderId, bls]) => {
      const bl = bls.find((d) => String(d.id) === dnId);
      if (bl) {
        foundOrderId = orderId;
        foundBl = bl;
      }
    });

    if (foundOrderId && foundBl) {
      const order = orders.find((o) => String(o.id) === String(foundOrderId)) || null;
      setSelectedOrder(order);
      setDeliveryNotes(blByOrder[foundOrderId] || []);
      setSelectedDeliveryNote(foundBl);
      setFormData((prev) => ({
        ...prev,
        orderId: String(foundOrderId),
        deliveryNoteId: dnId,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preselectedDeliveryNoteId, blByOrder, orders]);

  const fetchBillableData = async () => {
    try {
      const [ordersData, blsData] = await Promise.all([
        getAllOrders(),
        getAllDeliveryNotes(),
      ]);
      const ordersList = Array.isArray(ordersData) ? ordersData : [];
      const blsList = Array.isArray(blsData) ? blsData : (blsData?.data || []);

      // BL livrés et non encore facturés, regroupés par commande
      const map = {};
      blsList
        .filter((dn) => dn.status === "DELIVERED" && !dn.invoiceNumber)
        .forEach((dn) => {
          if (!map[dn.orderId]) map[dn.orderId] = [];
          map[dn.orderId].push(dn);
        });
      setBlByOrder(map);

      // On ne propose que les commandes ayant au moins un BL à facturer
      const billableOrders = ordersList
        .filter((o) => map[o.id] && map[o.id].length > 0)
        .sort((a, b) => (b.orderNumber || "").localeCompare(a.orderNumber || ""));
      setOrders(billableOrders);
    } catch (err) {
      setError("Impossible de charger les commandes facturables");
      console.error(err);
    }
  };

  const fetchInvoiceDetails = async () => {
    setLoading(true);
    try {
      const invoiceData = await getInvoiceById(id);

      // Charger le BL associé (figé en édition)
      if (invoiceData.deliveryNoteId) {
        const bl = await getDeliveryNoteById(invoiceData.deliveryNoteId);
        setSelectedDeliveryNote(bl);
        setDeliveryNotes([bl]);
      }

      // Charger la commande associée
      const orderId = invoiceData.order?.id || invoiceData.orderId;
      if (orderId) {
        const orderData = await getOrderById(orderId);
        setSelectedOrder(orderData);
        setOrders((prev) =>
          prev.some((o) => o.id === orderData.id) ? prev : [orderData, ...prev]
        );
      }

      const discountType = invoiceData.discountPercent > 0 ? "percent" : "fixed";

      setFormData({
        orderId: orderId || "",
        deliveryNoteId: invoiceData.deliveryNoteId || "",
        issueDate: (invoiceData.issueDate || "").split("T")[0],
        dueDate: (invoiceData.dueDate || "").split("T")[0],
        discountType,
        discountAmount: invoiceData.discountAmount || "0",
        discountPercent: invoiceData.discountPercent || "0",
        notes: invoiceData.notes || "",
        paymentTerms: invoiceData.paymentTerms?.toString() || "30",
        status: invoiceData.status,
      });
    } catch (err) {
      setError("Impossible de charger les détails de la facture");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // ───────────────────────── Date d'échéance auto ─────────────────────────
  useEffect(() => {
    if (formData.issueDate && formData.paymentTerms !== "") {
      const issue = new Date(formData.issueDate);
      const due = new Date(issue);
      due.setDate(due.getDate() + parseInt(formData.paymentTerms, 10));
      setFormData((prev) => ({ ...prev, dueDate: due.toISOString().split("T")[0] }));
    }
  }, [formData.issueDate, formData.paymentTerms]);

  // ───────────────────────── Calcul des montants ─────────────────────────
  useEffect(() => {
    calculateAmounts();
  }, [
    selectedDeliveryNote,
    selectedOrder,
    formData.discountAmount,
    formData.discountPercent,
    formData.discountType,
  ]);

  // HT du BL = Σ (quantité livrée × prix unitaire figé)
  const computeBLHT = (dn) => {
    if (!dn || !Array.isArray(dn.items)) return 0;
    return dn.items.reduce(
      (sum, it) =>
        sum + (Number(it.deliveredQuantity) || 0) * (Number(it.unitPrice) || 0),
      0
    );
  };

  const calculateAmounts = () => {
    if (!selectedDeliveryNote) {
      setCalculatedAmounts({
        totalHT: 0,
        totalAfterDiscount: 0,
        discountValue: 0,
        TVA: 0,
        totalTTC: 0,
      });
      return;
    }

    const totalHT = computeBLHT(selectedDeliveryNote);
    let discountValue = 0;
    if (formData.discountType === "fixed") {
      discountValue = parseFloat(formData.discountAmount) || 0;
    } else {
      const pct = parseFloat(formData.discountPercent) || 0;
      discountValue = (totalHT * pct) / 100;
    }

    if (discountValue > totalHT) {
      setError("La remise ne peut pas dépasser le montant HT");
      discountValue = totalHT;
    } else if (error === "La remise ne peut pas dépasser le montant HT") {
      setError("");
    }

    const totalAfterDiscount = totalHT - discountValue;
    const TVA = selectedOrder?.vatApplicable ? totalAfterDiscount * 0.18 : 0;
    const totalTTC = totalAfterDiscount + TVA;

    setCalculatedAmounts({
      totalHT,
      totalAfterDiscount,
      discountValue,
      TVA,
      totalTTC,
    });
  };

  // ───────────────────────── Sélections ─────────────────────────
  const handleOrderSelect = (e) => {
    const orderId = e.target.value;
    setSelectedDeliveryNote(null);
    setFormData((prev) => ({ ...prev, orderId, deliveryNoteId: "" }));

    if (!orderId) {
      setSelectedOrder(null);
      setDeliveryNotes([]);
      return;
    }

    const order = orders.find((o) => String(o.id) === String(orderId)) || null;
    setSelectedOrder(order);
    setDeliveryNotes(blByOrder[orderId] || []);
  };

  const handleDeliveryNoteSelect = (e) => {
    const dnId = e.target.value;
    setFormData((prev) => ({ ...prev, deliveryNoteId: dnId }));
    const dn = deliveryNotes.find((d) => String(d.id) === String(dnId)) || null;
    setSelectedDeliveryNote(dn);
  };

  const handleDiscountTypeChange = (e) => {
    const discountType = e.target.value;
    setFormData((prev) => ({
      ...prev,
      discountType,
      discountAmount: discountType === "fixed" ? prev.discountAmount : "0",
      discountPercent: discountType === "percent" ? prev.discountPercent : "0",
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // ───────────────────────── Soumission ─────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    setSuccess("");

    try {
      if (!formData.deliveryNoteId) {
        throw new Error("Veuillez sélectionner un bon de livraison");
      }

      const totalHT = computeBLHT(selectedDeliveryNote);
      let discountValue = 0;
      if (formData.discountType === "fixed") {
        discountValue = parseFloat(formData.discountAmount) || 0;
      } else {
        const pct = parseFloat(formData.discountPercent) || 0;
        discountValue = (totalHT * pct) / 100;
      }
      if (discountValue > totalHT) {
        throw new Error("La remise ne peut pas dépasser le montant HT");
      }

      const invoiceData = {
        deliveryNoteId: Number(formData.deliveryNoteId),
        orderId: selectedOrder ? Number(selectedOrder.id) : null,
        clientId: selectedOrder?.partner ? Number(selectedOrder.partner.id) : null,
        issueDate: formData.issueDate,
        dueDate: formData.dueDate,
        paymentTerms: parseInt(formData.paymentTerms, 10),
        notes: formData.notes,
        status: formData.status,
        totalAmount: totalHT,
        discountAmount:
          formData.discountType === "fixed"
            ? parseFloat(formData.discountAmount) || 0
            : discountValue,
        discountPercent:
          formData.discountType === "percent"
            ? parseFloat(formData.discountPercent) || 0
            : 0,
      };

      if (isEditMode) {
        await updateInvoice(id, invoiceData);
        setSuccess("Facture mise à jour avec succès");
      } else {
        await createInvoice(invoiceData);
        setSuccess("Facture créée avec succès");
        setFormData({
          orderId: "",
          deliveryNoteId: "",
          issueDate: new Date().toISOString().split("T")[0],
          dueDate: "",
          discountType: "fixed",
          discountAmount: "0",
          discountPercent: "0",
          notes: "",
          paymentTerms: "30",
          status: "PENDING",
        });
        setSelectedOrder(null);
        setSelectedDeliveryNote(null);
        setDeliveryNotes([]);
      }

      setTimeout(() => onSuccess && onSuccess(), 2000);
    } catch (err) {
      setError(
        err.message ||
          "Une erreur est survenue lors de l'enregistrement de la facture"
      );
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const blLabel = (dn) => {
    const ht = computeBLHT(dn);
    const date = dn.deliveryDate || dn.createdAt;
    const dateStr = date ? new Date(date).toLocaleDateString() : "";
    return `${dn.referenceNumber} — ${dateStr} — ${formatCurrency(ht)}`;
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <Card className="shadow-sm">
      <Card.Header>
        <Row className="align-items-center">
          <Col>
            <h5 className="mb-0">
              {isEditMode ? "Modifier la facture" : "Créer une nouvelle facture"}
            </h5>
          </Col>
          <Col xs="auto">
            <Button variant="outline-secondary" onClick={() => onCancel()}>
              <ArrowLeft size={16} className="me-1" />
              Retour
            </Button>
          </Col>
        </Row>
      </Card.Header>
      <Card.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}

        <Form onSubmit={handleSubmit}>
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group controlId="orderId">
                <Form.Label>Commande</Form.Label>
                <InputGroup>
                  <InputGroup.Text>
                    <ShoppingCart size={16} />
                  </InputGroup.Text>
                  <Form.Select
                    name="orderId"
                    value={formData.orderId}
                    onChange={handleOrderSelect}
                    required
                    disabled={isEditMode}
                  >
                    <option value="">Sélectionner une commande</option>
                    {orders.map((order) => (
                      <option key={order.id} value={order.id}>
                        {order.orderNumber}
                        {order.partner ? ` — ${order.partner.name}` : ""}
                        {order.vatApplicable ? " (TVA)" : " (exonéré)"}
                      </option>
                    ))}
                  </Form.Select>
                </InputGroup>
                <Form.Text className="text-muted">
                  Seules les commandes ayant un BL à facturer sont listées.
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="deliveryNoteId">
                <Form.Label>Bon de livraison à facturer</Form.Label>
                <InputGroup>
                  <InputGroup.Text>
                    <Truck size={16} />
                  </InputGroup.Text>
                  <Form.Select
                    name="deliveryNoteId"
                    value={formData.deliveryNoteId}
                    onChange={handleDeliveryNoteSelect}
                    required
                    disabled={isEditMode || !formData.orderId}
                  >
                    <option value="">Sélectionner un bon de livraison</option>
                    {deliveryNotes.map((dn) => (
                      <option key={dn.id} value={dn.id}>
                        {blLabel(dn)}
                      </option>
                    ))}
                  </Form.Select>
                </InputGroup>
                <Form.Text className="text-muted">
                  {formData.orderId && deliveryNotes.length === 0
                    ? "Aucun bon de livraison facturable pour cette commande."
                    : "Seuls les BL livrés et non encore facturés sont proposés."}
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          {selectedOrder && (
            <Card className="mb-3 bg-light">
              <Card.Body>
                <h6 className="mb-3">Détails</h6>
                <Row>
                  <Col md={3}>
                    <small className="text-muted">Commande</small>
                    <p className="mb-2">{selectedOrder.orderNumber}</p>
                  </Col>
                  <Col md={3}>
                    <small className="text-muted">Client</small>
                    <p className="mb-2">{selectedOrder.partner?.name || "—"}</p>
                  </Col>
                  <Col md={3}>
                    <small className="text-muted">TVA</small>
                    <p className="mb-2">
                      {selectedOrder.vatApplicable ? (
                        <Badge bg="info">Assujetti (18%)</Badge>
                      ) : (
                        <Badge bg="warning">Exonéré</Badge>
                      )}
                    </p>
                  </Col>
                  <Col md={3}>
                    <small className="text-muted">BL sélectionné</small>
                    <p className="mb-2">
                      {selectedDeliveryNote?.referenceNumber || "—"}
                    </p>
                  </Col>
                </Row>

                {selectedDeliveryNote &&
                  Array.isArray(selectedDeliveryNote.items) && (
                    <div className="mt-2">
                      <small className="text-muted">Lignes livrées</small>
                      <ul className="mb-0">
                        {selectedDeliveryNote.items.map((it) => (
                          <li key={it.id}>
                            {it.productName} — {it.deliveredQuantity} ×{" "}
                            {formatCurrency(it.unitPrice)} ={" "}
                            {formatCurrency(
                              (Number(it.deliveredQuantity) || 0) *
                                (Number(it.unitPrice) || 0)
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
              </Card.Body>
            </Card>
          )}

          {selectedDeliveryNote && (
            <Card className="mb-3">
              <Card.Body>
                <h6>Remise</h6>
                <Row className="mt-2">
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>Type de remise</Form.Label>
                      <Form.Select
                        name="discountType"
                        value={formData.discountType}
                        onChange={handleDiscountTypeChange}
                      >
                        <option value="fixed">Montant fixe</option>
                        <option value="percent">Pourcentage</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    {formData.discountType === "fixed" ? (
                      <Form.Group controlId="discountAmount">
                        <Form.Label>Remise (montant)</Form.Label>
                        <InputGroup>
                          <InputGroup.Text>
                            <Hash size={16} />
                          </InputGroup.Text>
                          <Form.Control
                            type="number"
                            min="0"
                            onWheel={(e) => e.target.blur()}
                            step="0.01"
                            name="discountAmount"
                            value={formData.discountAmount}
                            onChange={handleInputChange}
                          />
                        </InputGroup>
                      </Form.Group>
                    ) : (
                      <Form.Group controlId="discountPercent">
                        <Form.Label>Remise (pourcentage)</Form.Label>
                        <InputGroup>
                          <InputGroup.Text>
                            <Percent size={16} />
                          </InputGroup.Text>
                          <Form.Control
                            type="number"
                            min="0"
                            max="100"
                            onWheel={(e) => e.target.blur()}
                            step="0.01"
                            name="discountPercent"
                            value={formData.discountPercent}
                            onChange={handleInputChange}
                          />
                          <InputGroup.Text>%</InputGroup.Text>
                        </InputGroup>
                      </Form.Group>
                    )}
                  </Col>
                </Row>

                <hr />
                <h6>Récapitulatif financier</h6>
                <Row className="mt-3">
                  <Col md={3}>
                    <small className="text-muted">Montant HT</small>
                    <p>{formatCurrency(calculatedAmounts.totalHT)}</p>
                  </Col>
                  <Col md={3}>
                    <small className="text-muted">Après remise</small>
                    <p>{formatCurrency(calculatedAmounts.totalAfterDiscount)}</p>
                  </Col>
                  <Col md={3}>
                    <small className="text-muted">TVA (18%)</small>
                    <p>{formatCurrency(calculatedAmounts.TVA)}</p>
                    {!selectedOrder?.vatApplicable && (
                      <small className="text-muted d-block">
                        Commande exonérée de TVA
                      </small>
                    )}
                  </Col>
                  <Col md={3}>
                    <small className="text-muted">Montant TTC</small>
                    <p className="fw-bold">
                      {formatCurrency(calculatedAmounts.totalTTC)}
                    </p>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          )}

          <Row className="mb-3">
            <Col md={6}>
              <Form.Group controlId="issueDate">
                <Form.Label>Date d'émission</Form.Label>
                <InputGroup>
                  <InputGroup.Text>
                    <Calendar size={16} />
                  </InputGroup.Text>
                  <Form.Control
                    type="date"
                    name="issueDate"
                    value={formData.issueDate}
                    onChange={handleInputChange}
                    required
                  />
                </InputGroup>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="paymentTerms">
                <Form.Label>Conditions de paiement</Form.Label>
                <Form.Select
                  name="paymentTerms"
                  value={formData.paymentTerms}
                  onChange={handleInputChange}
                  required
                >
                  <option value="0">Paiement immédiat</option>
                  <option value="15">15 jours</option>
                  <option value="30">30 jours</option>
                  <option value="45">45 jours</option>
                  <option value="60">60 jours</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={6}>
              <Form.Group controlId="dueDate">
                <Form.Label>Date d'échéance</Form.Label>
                <InputGroup>
                  <InputGroup.Text>
                    <Calendar size={16} />
                  </InputGroup.Text>
                  <Form.Control
                    type="date"
                    name="dueDate"
                    value={formData.dueDate}
                    onChange={handleInputChange}
                    required
                    readOnly
                  />
                </InputGroup>
                <Form.Text className="text-muted">
                  Calculée automatiquement selon les conditions de paiement
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="status">
                <Form.Label>Statut</Form.Label>
                <Form.Select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  required
                >
                  <option value="PENDING">En attente</option>
                  <option value="PAID">Payée</option>
                  <option value="CANCELLED">Annulée</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={12}>
              <Form.Group controlId="notes">
                <Form.Label>Notes / Commentaires</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  placeholder="Notes ou commentaires additionnels pour cette facture..."
                />
              </Form.Group>
            </Col>
          </Row>

          <div className="d-flex justify-content-end">
            <Button
              variant="secondary"
              className="me-2"
              onClick={() => onCancel()}
              disabled={submitting}
            >
              Annuler
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={submitting || !formData.deliveryNoteId}
            >
              <Save size={16} className="me-1" />
              {submitting
                ? "Enregistrement..."
                : isEditMode
                ? "Modifier"
                : "Créer"}
            </Button>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default InvoiceForm;